import React from "react";
import {
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Launch as LaunchIcon,
  NotificationImportant as ReminderIcon,
} from "@mui/icons-material";
import { ActionLogErrorIndicator } from "./ActionLogErrorIndicator";
import { ActionLogInfo } from "@services/admin/types";

interface ActionLogActionButtonsProps {
  log: ActionLogInfo;
  onViewDetails: (log: ActionLogInfo) => void;
  onViewGptRequest?: (gptRequestId: string) => void;
  onViewExtractedData?: (extractedDataId: string) => void;
  onViewDerivedData?: (derivedDataId: string) => void;
  onViewShipmentEmail?: (shipmentEmailId: string) => void;
  onViewShipmentFeed?: (shipmentFeedId: string) => void;
  onViewAIAgentTrace?: (log: ActionLogInfo) => void;
  onViewErrors?: (log: ActionLogInfo) => void;
  onViewReminder?: (reminderId: string) => void;
  showErrorIndicator?: boolean;
}

export function ActionLogActionButtons({
  log,
  onViewDetails,
  onViewGptRequest,
  onViewExtractedData,
  onViewDerivedData,
  onViewShipmentEmail,
  onViewShipmentFeed,
  onViewAIAgentTrace,
  onViewErrors,
  onViewReminder,
  showErrorIndicator = true,
}: ActionLogActionButtonsProps) {
  return (
    <Stack direction="row" spacing={1}>
      {showErrorIndicator && (
        <ActionLogErrorIndicator
          log={log}
          onClick={onViewErrors ? () => onViewErrors(log) : undefined}
        />
      )}
      
      {(log.type === "StartAIAgent" || log.type === "EndAIAgent") &&
        (log.extra?.trace_id || log.extra?.openai_trace_url || log.extra?.traceId) &&
        onViewAIAgentTrace && (
          <Tooltip title="View OpenAI Trace">
            <IconButton
              size="small"
              onClick={() => onViewAIAgentTrace(log)}
            >
              <LaunchIcon />
            </IconButton>
          </Tooltip>
        )}
      
      {log.gptRequestId && onViewGptRequest && (
        <Tooltip title="View GPT Request">
          <IconButton
            size="small"
            onClick={() => onViewGptRequest(log.gptRequestId!)}
          >
            <LaunchIcon />
          </IconButton>
        </Tooltip>
      )}
      
      {log.extractedDataId && onViewExtractedData && (
        <Tooltip title="View Extracted Data">
          <IconButton
            size="small"
            onClick={() => onViewExtractedData(log.extractedDataId!)}
          >
            <LaunchIcon />
          </IconButton>
        </Tooltip>
      )}
      
      {log.derivedDataId && onViewDerivedData && (
        <Tooltip title="View Derived Data">
          <IconButton
            size="small"
            onClick={() => onViewDerivedData(log.derivedDataId!)}
          >
            <LaunchIcon />
          </IconButton>
        </Tooltip>
      )}

      {log.shipmentEmailId && onViewShipmentEmail && (
        <Tooltip title="View Shipment Email">
          <IconButton
            size="small"
            onClick={() => onViewShipmentEmail(log.shipmentEmailId!)}
            color="secondary"
          >
            <LaunchIcon />
          </IconButton>
        </Tooltip>
      )}

      {log.shipmentFeedId && onViewShipmentFeed && (
        <Tooltip title="View Shipment Feed">
          <IconButton
            size="small"
            onClick={() => onViewShipmentFeed(log.shipmentFeedId!)}
            color="secondary"
          >
            <LaunchIcon />
          </IconButton>
        </Tooltip>
      )}

      {log.reminderId && onViewReminder && (
        <Tooltip title="View Reminder">
          <IconButton
            size="small"
            onClick={() => onViewReminder(log.reminderId!)}
            color="warning"
          >
            <ReminderIcon />
          </IconButton>
        </Tooltip>
      )}

      <IconButton
        size="small"
        onClick={() => onViewDetails(log)}
        title="View Details"
      >
        <VisibilityIcon />
      </IconButton>
    </Stack>
  );
}